/**
 * DisclosureOS Wired Hub — 16 Live User Journeys
 * 
 * Connects existing JSX mockups to live backend API endpoints.
 * Uses apiFetch from api_service.js to prevent the "clone" error.
 * 
 * Coverage: 16 features across 9 of 11 sidebar sections.
 * UC-03, UC-06, UC-07, UC-08, UC-09, UC-10, UC-11, UC-13,
 * UC-14, UC-15, UC-17, UC-21, UC-22, UC-23, UC-24, UC-27
 */
import { useState, useEffect } from "react";
import { apiFetch } from "../services/api_service";

const C = {
  bg:"#050a14",surface:"#0c1220",card:"#111c30",cardHover:"#162040",
  border:"#1a2744",borderHi:"#2a3a5c",text:"#e2e8f0",muted:"#6b7a96",dim:"#384660",
  blue:"#3b82f6",amber:"#f59e0b",emerald:"#10b981",purple:"#8b5cf6",
  cyan:"#06b6d4",red:"#ef4444",pink:"#ec4899",teal:"#14b8a6",orange:"#f97316",white:"#fff",
};

const TABS = [
  {id:"materiality",label:"Materiality",uc:"UC-03",color:C.purple,icon:"M"},
  {id:"scope3",label:"Scope 3",uc:"UC-06",color:C.cyan,icon:"S3"},
  {id:"claims",label:"AI Claims",uc:"UC-08",color:C.blue,icon:"AI"},
  {id:"crossmap",label:"Cross-Map",uc:"UC-09",color:C.teal,icon:"CM"},
  {id:"ef",label:"EF Search",uc:"UC-10",color:C.emerald,icon:"EF"},
  {id:"dq",label:"DQ Check",uc:"UC-11",color:C.amber,icon:"DQ"},
  {id:"eval",label:"RAGAS Eval",uc:"UC-14",color:C.purple,icon:"RE"},
  {id:"shock",label:"M&A Shock",uc:"UC-15",color:C.red,icon:"MA"},
  {id:"taxonomy",label:"Taxonomy",uc:"UC-17",color:C.emerald,icon:"TX"},
  {id:"supplier",label:"Suppliers",uc:"UC-07",color:C.pink,icon:"SP"},
  {id:"auditor",label:"Auditor",uc:"UC-13",color:C.emerald,icon:"AU"},
  {id:"csddd",label:"CSDDD",uc:"UC-21",color:C.red,icon:"DD"},
  {id:"cbam",label:"CBAM",uc:"UC-22",color:C.orange,icon:"CB"},
  {id:"benchmark",label:"Benchmark",uc:"UC-23",color:C.cyan,icon:"BM"},
  {id:"consistency",label:"Consistency",uc:"UC-24",color:C.blue,icon:"CC"},
  {id:"pii",label:"PII Scan",uc:"UC-27",color:C.amber,icon:"PI"},
];

// ── Shared UI primitives ──
const Btn = ({onClick,children,color=C.blue,disabled,small,"data-testid":testId}) => (
  <button data-testid={testId} onClick={onClick} disabled={disabled} style={{padding:small?"4px 10px":"7px 16px",borderRadius:6,border:`1px solid ${color}44`,background:disabled?C.dim+"33":color+"18",color:disabled?C.dim:color,fontSize:small?10:11,fontWeight:600,cursor:disabled?"not-allowed":"pointer",transition:"all 0.2s"}}>{children}</button>
);
const Badge = ({text,color=C.teal}) => (
  <span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:3,background:color,color:C.bg}}>{text}</span>
);
const Card = ({children,color=C.border,style={}}) => (
  <div style={{padding:"12px 14px",borderRadius:8,background:C.card,border:`1px solid ${color}33`,marginBottom:8,...style}}>{children}</div>
);
const Label = ({children}) => <div style={{fontSize:10,color:C.muted,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.5px"}}>{children}</div>;
const Val = ({children,color=C.text}) => <div style={{fontSize:14,fontWeight:600,color}}>{children}</div>;
const Input = ({value,onChange,placeholder,style={}}) => (
  <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{padding:"6px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:11,width:"100%",...style}}/>
);
const TextArea = ({value,onChange,placeholder,rows=4}) => (
  <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows} style={{padding:"8px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:11,width:"100%",resize:"vertical",fontFamily:"inherit"}}/>
);
const Loader = ({text="Loading..."}) => <div style={{textAlign:"center",padding:20,color:C.muted,fontSize:12}}>{text}</div>;
const Err = ({error}) => error ? <div style={{padding:8,borderRadius:5,background:C.red+"15",border:`1px solid ${C.red}33`,color:C.red,fontSize:11,marginBottom:8}}>{error}</div> : null;

// ── API helpers using apiFetch (prevents clone error) ──
async function api(path) {
  const data = await apiFetch(path);
  if (data && data._error) throw new Error(data.message || "Request failed");
  return data;
}
async function post(path, body) {
  const data = await apiFetch(path, { method: "POST", body });
  if (data && data._error) throw new Error(data.message || "Request failed");
  return data;
}
async function put(path, body) {
  const data = await apiFetch(path, { method: "PUT", body });
  if (data && data._error) throw new Error(data.message || "Request failed");
  return data;
}

// ═══ UC-03: DOUBLE MATERIALITY ═══
function MaterialityTab() {
  const [industry,setIndustry] = useState("manufacturing");
  const [result,setResult] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");
  const [iro,setIro] = useState(null);

  const assess = async () => {
    setLoading(true); setError("");
    try {
      const r = await post("/materiality/assess-industry",{industry});
      setResult(r);
      try { const i = await api("/iro/matrix"); setIro(i); } catch {}
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div data-testid="materiality-tab">
      <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"flex-end"}}>
        <div style={{flex:1}}><Label>Industry / Sector</Label>
          <select data-testid="materiality-industry-select" value={industry} onChange={e=>setIndustry(e.target.value)} style={{padding:"6px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:11,width:"100%"}}>
            {["manufacturing","energy","chemicals","construction","finance","technology","automotive","mining","pharma","agriculture"].map(i=>
              <option key={i} value={i}>{i.charAt(0).toUpperCase()+i.slice(1)}</option>)}
          </select>
        </div>
        <Btn data-testid="run-dma-btn" onClick={assess} color={C.purple} disabled={loading}>{loading?"Assessing...":"Run DMA"}</Btn>
      </div>
      <Err error={error}/>
      {result && (
        <div data-testid="materiality-results">
          <Card color={C.purple}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div><Label>Material Topics</Label><Val color={C.purple}>{result.material_topics?.length || result.topics?.length || 0} topics identified</Val></div>
              <Badge text="DOUBLE MATERIALITY" color={C.purple}/>
            </div>
          </Card>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {(result.material_topics || result.topics || []).map((t,i) => (
              <Card key={i} color={C.purple} style={{padding:"8px 10px"}}>
                <div style={{fontSize:11,fontWeight:600,color:C.text}}>{t.topic || t.name || t}</div>
                <div style={{fontSize:9,color:C.muted}}>{t.verdict || t.impact || "Material"}</div>
              </Card>
            ))}
          </div>
        </div>
      )}
      {iro && iro.assessments && (
        <div style={{marginTop:12}}>
          <Label>IRO Matrix ({iro.assessments.length} assessments)</Label>
          {iro.assessments.slice(0,8).map((a,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${C.border}`,fontSize:10}}>
              <span style={{color:C.text}}>{a.topic || a.esrs_topic}</span>
              <span style={{color:a.verdict==="material"?C.emerald:C.amber}}>{a.verdict}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ UC-06: SCOPE 3 ESTIMATION ═══
function Scope3Tab() {
  const [spendData,setSpendData] = useState("IT Services,250000\nChemical Supplies,180000\nBusiness Travel,95000\nLogistics,320000");
  const [result,setResult] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");

  const estimate = async () => {
    setLoading(true); setError("");
    try {
      const lines = spendData.split("\n").filter(l=>l.trim());
      const items = lines.map(l => {const [desc,amt]=l.split(","); return {description:desc.trim(),amount:parseFloat(amt)||0,currency:"EUR"};});
      const r = await post("/ai/scope3/estimate",{spend_items:items,entity_id:"default"});
      setResult(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div data-testid="scope3-tab">
      <Label>Spend Data (description, amount per line)</Label>
      <TextArea value={spendData} onChange={setSpendData} rows={5} placeholder="IT Services,250000"/>
      <div style={{marginTop:8}}><Btn data-testid="estimate-scope3-btn" onClick={estimate} color={C.cyan} disabled={loading}>{loading?"Estimating...":"Estimate Scope 3"}</Btn></div>
      <Err error={error}/>
      {result && (
        <div data-testid="scope3-results" style={{marginTop:12}}>
          <Card color={C.cyan}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <div><Label>Total Scope 3</Label><Val color={C.cyan}>{(result.total_tco2e || result.total || 0).toLocaleString()} tCO2e</Val></div>
              <div><Label>Categories</Label><Val>{result.categories?.length || result.items?.length || 0}</Val></div>
              <div><Label>Confidence</Label><Val color={C.amber}>{result.confidence || result.methodology || "Spend-based"}</Val></div>
            </div>
          </Card>
          {(result.items || result.categories || []).map((it,i) => (
            <Card key={i} style={{padding:"8px 10px"}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11}}>
                <span style={{fontWeight:600,color:C.text}}>{it.description || it.category || it.naics_description}</span>
                <span style={{color:C.cyan,fontWeight:600}}>{(it.tco2e || it.emissions || 0).toLocaleString()} tCO2e</span>
              </div>
              <div style={{fontSize:9,color:C.muted}}>NAICS: {it.naics || it.naics_code || "—"} | Factor: {it.factor || "EEIO"} kgCO2e/EUR</div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ UC-08: AI CLAIM GENERATION ═══
function ClaimsTab() {
  const [reqs,setReqs] = useState([]);
  const [drafts,setDrafts] = useState([]);
  const [selected,setSelected] = useState("");
  const [generating,setGenerating] = useState(false);
  const [generated,setGenerated] = useState(null);
  const [error,setError] = useState("");

  useEffect(() => {
    api("/requirements/").then(r => setReqs(r.requirements || [])).catch(()=>{});
    api("/drafts/claims").then(r => setDrafts(r.drafts || r.claims || [])).catch(()=>{});
  }, []);

  const generate = async () => {
    if (!selected) return;
    setGenerating(true); setError("");
    try {
      const r = await post("/ai/generate-claim",{requirement_id:selected,entity_id:"default"});
      setGenerated(r);
      try { const d = await api("/drafts/claims"); setDrafts(d.drafts || d.claims || []); } catch {}
    } catch(e) { setError(e.message); }
    setGenerating(false);
  };

  const promote = async (draftId) => {
    try {
      await post(`/drafts/claims/${draftId}/promote`,{approver:"user",feedback:"Approved via UI"});
      const d = await api("/drafts/claims"); setDrafts(d.drafts || d.claims || []);
    } catch(e) { setError(e.message); }
  };

  return (
    <div data-testid="claims-tab">
      <div style={{display:"flex",gap:8,marginBottom:12,alignItems:"flex-end"}}>
        <div style={{flex:1}}><Label>Select Requirement</Label>
          <select data-testid="claims-requirement-select" value={selected} onChange={e=>setSelected(e.target.value)} style={{padding:"6px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:11,width:"100%"}}>
            <option value="">— Select requirement —</option>
            {reqs.slice(0,20).map(r => <option key={r.requirement_id} value={r.requirement_id}>{r.reference_code || r.requirement_id} — {(r.legal_text||"").slice(0,60)}</option>)}
          </select>
        </div>
        <Btn data-testid="generate-claim-btn" onClick={generate} color={C.blue} disabled={generating||!selected}>{generating?"Generating...":"Generate Claim"}</Btn>
      </div>
      <Err error={error}/>
      {generated && (
        <Card data-testid="generated-claim" color={C.blue}>
          <Label>AI-Generated Claim</Label>
          <div style={{fontSize:12,color:C.text,lineHeight:1.6,marginBottom:8}}>{generated.claim_text || generated.draft?.text || JSON.stringify(generated).slice(0,300)}</div>
          <div style={{display:"flex",gap:8,fontSize:9}}>
            <span style={{color:C.emerald}}>Confidence: {generated.confidence || generated.quality_score || "—"}</span>
            <span style={{color:C.amber}}>Citations: {generated.citations?.length || 0}</span>
            <span style={{color:C.purple}}>Anti-hallucination: {generated.ah_checks || "5 layers"}</span>
          </div>
        </Card>
      )}
      {drafts.length > 0 && (
        <div style={{marginTop:12}}>
          <Label>Draft Claims ({drafts.length})</Label>
          {drafts.slice(0,10).map((d,i) => (
            <Card key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:11,fontWeight:600,color:C.text}}>{d.requirement_id || d.ref_code}</div>
                <div style={{fontSize:10,color:C.muted}}>{(d.text || d.claim_text || "").slice(0,80)}...</div>
              </div>
              <div style={{display:"flex",gap:4,alignItems:"center"}}>
                <Badge text={d.status || "draft"} color={d.status==="promoted"?C.emerald:C.amber}/>
                {d.status !== "promoted" && <Btn onClick={()=>promote(d.draft_id || d.id)} color={C.emerald} small>Promote</Btn>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ UC-09: CROSS-FRAMEWORK MAP ═══
function CrossMapTab() {
  const [evidence,setEvidence] = useState("Scope 1 GHG emissions: 127,450 tCO2e in FY2024. GHG Protocol, operational control. DEFRA 2024 factors.");
  const [result,setResult] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");

  const suggest = async () => {
    setLoading(true); setError("");
    try {
      const r = await post("/ai/crossmap/suggest",{text:evidence,entity_id:"default"});
      setResult(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div data-testid="crossmap-tab">
      <Label>Evidence Text</Label>
      <TextArea value={evidence} onChange={setEvidence} rows={3}/>
      <div style={{marginTop:8}}><Btn data-testid="crossmap-btn" onClick={suggest} color={C.teal} disabled={loading}>{loading?"Mapping...":"Map to All Frameworks"}</Btn></div>
      <Err error={error}/>
      {result && (
        <div data-testid="crossmap-results" style={{marginTop:12}}>
          <Card color={C.teal}><Label>Cross-Framework Mappings</Label><Val color={C.teal}>{result.mappings?.length || result.frameworks?.length || 0} frameworks matched</Val></Card>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
            {(result.mappings || result.frameworks || []).map((m,i) => (
              <Card key={i} color={C.teal} style={{padding:"8px 10px"}}>
                <div style={{fontSize:11,fontWeight:600,color:C.teal}}>{m.framework || m.target_framework}</div>
                <div style={{fontSize:10,color:C.text}}>{m.requirement || m.reference_code || m.datapoint}</div>
                <div style={{fontSize:9,color:C.muted}}>Confidence: {m.confidence || m.score || "high"}</div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══ UC-10: EMISSION FACTOR SEARCH ═══
function EFSearchTab() {
  const [query,setQuery] = useState("natural gas");
  const [geo,setGeo] = useState("");
  const [scope,setScope] = useState("");
  const [results,setResults] = useState([]);
  const [loading,setLoading] = useState(false);

  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({q:query}); if(geo) params.set("geography",geo); if(scope) params.set("scope",scope);
      const r = await api(`/data/search/ef?${params}`);
      setResults(r.results || []);
    } catch {}
    setLoading(false);
  };

  return (
    <div data-testid="ef-search-tab">
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        <div style={{flex:2}}><Input value={query} onChange={setQuery} placeholder="natural gas, diesel, electricity..."/></div>
        <div style={{flex:1}}><Input value={geo} onChange={setGeo} placeholder="Geography (UK, DE...)"/></div>
        <div style={{flex:1}}>
          <select value={scope} onChange={e=>setScope(e.target.value)} style={{padding:"6px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:11,width:"100%"}}>
            <option value="">All scopes</option><option value="1">Scope 1</option><option value="2">Scope 2</option><option value="3">Scope 3</option>
          </select>
        </div>
        <Btn data-testid="ef-search-btn" onClick={search} color={C.emerald} disabled={loading}>{loading?"...":"Search"}</Btn>
      </div>
      {results.length > 0 && (
        <div data-testid="ef-results" style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
            <thead><tr style={{borderBottom:`1px solid ${C.border}`}}>
              {["Activity","Category","Geography","Scope","Factor","Unit","Source","Year"].map(h=>
                <th key={h} style={{padding:"6px 8px",textAlign:"left",color:C.muted,fontWeight:600}}>{h}</th>)}
            </tr></thead>
            <tbody>{results.map((r,i)=>(
              <tr key={i} style={{borderBottom:`1px solid ${C.border}20`}}>
                <td style={{padding:"5px 8px",color:C.text}}>{r.activity}</td>
                <td style={{color:C.muted}}>{r.category}</td>
                <td><Badge text={r.geography} color={C.blue}/></td>
                <td style={{color:C.muted}}>{r.scope}</td>
                <td style={{color:C.emerald,fontWeight:700}}>{r.value_total}</td>
                <td style={{color:C.muted,fontSize:9}}>{r.unit_out}/{r.unit_in}</td>
                <td style={{color:C.muted}}>{r.source}</td>
                <td style={{color:C.dim}}>{r.year}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══ UC-11: DATA QUALITY CHECK ═══
function DQCheckTab() {
  const [metric,setMetric] = useState("scope1_emissions");
  const [value,setValue] = useState("127450");
  const [unit,setUnit] = useState("tCO2e");
  const [result,setResult] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");

  const check = async () => {
    setLoading(true); setError("");
    try {
      const r = await post("/ai/quality/check",{metric_name:metric,value:parseFloat(value),unit,entity_id:"default"});
      setResult(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const scoreColor = (s) => s>=4?C.emerald:s>=3?C.amber:C.red;

  return (
    <div data-testid="dq-check-tab">
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        <div style={{flex:1}}><Label>Metric</Label><Input value={metric} onChange={setMetric}/></div>
        <div style={{flex:1}}><Label>Value</Label><Input value={value} onChange={setValue}/></div>
        <div style={{flex:1}}><Label>Unit</Label><Input value={unit} onChange={setUnit}/></div>
      </div>
      <Btn data-testid="dq-check-btn" onClick={check} color={C.amber} disabled={loading}>{loading?"Checking...":"Run DQ Check"}</Btn>
      <Err error={error}/>
      {result && (
        <div data-testid="dq-results" style={{marginTop:12}}>
          <Card color={scoreColor(result.quality_score||0)}>
            <div style={{display:"flex",justifyContent:"space-between"}}>
              <div><Label>Quality Score</Label><Val color={scoreColor(result.quality_score||0)}>{result.quality_score || 0} / 5</Val></div>
              <div><Label>Peer Deviation</Label><Val>{result.peer_deviation || result.z_score || "—"}</Val></div>
              <div><Label>Flags</Label><Val color={C.amber}>{result.flags?.length || 0}</Val></div>
            </div>
          </Card>
          {(result.flags || result.checks || []).map((f,i) => (
            <div key={i} style={{padding:"5px 0",borderBottom:`1px solid ${C.border}20`,fontSize:10,color:f.severity==="error"?C.red:C.amber}}>
              {f.message || f.flag || f}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ UC-14: RAGAS EVALUATION ═══
function EvalTab() {
  const [result,setResult] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");

  useEffect(() => { api("/eval/latest").then(r=>setResult(r)).catch(()=>{}); }, []);

  const runEval = async () => {
    setLoading(true); setError("");
    try {
      const r = await post("/eval/run",{});
      setResult(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const metricBar = (label,val) => (
    <div style={{marginBottom:6}}>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,marginBottom:2}}>
        <span style={{color:C.muted}}>{label}</span><span style={{color:val>0.7?C.emerald:val>0.5?C.amber:C.red,fontWeight:600}}>{(val*100).toFixed(1)}%</span>
      </div>
      <div style={{height:6,borderRadius:3,background:C.dim+"33"}}>
        <div style={{height:6,borderRadius:3,background:val>0.7?C.emerald:val>0.5?C.amber:C.red,width:`${val*100}%`,transition:"width 0.5s"}}/>
      </div>
    </div>
  );

  return (
    <div data-testid="eval-tab">
      <Btn data-testid="run-eval-btn" onClick={runEval} color={C.purple} disabled={loading}>{loading?"Running RAGAS...":"Run Evaluation"}</Btn>
      <Err error={error}/>
      {result && result.eval_id && (
        <div data-testid="eval-results" style={{marginTop:12}}>
          <Card color={C.purple}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
              <div><Label>Eval ID</Label><Val>{result.eval_id?.slice(0,16)}</Val></div>
              <div><Label>Samples</Label><Val color={C.purple}>{result.total || 0}</Val></div>
              <div><Label>Pass Rate</Label><Val color={result.pass_rate>70?C.emerald:C.amber}>{result.pass_rate?.toFixed(1)}%</Val></div>
            </div>
            {metricBar("Faithfulness", result.faithfulness || 0)}
            {metricBar("Context Precision", result.context_precision || 0)}
            {metricBar("Answer Relevancy", result.answer_relevancy || 0)}
            {metricBar("Overall", result.overall_score || 0)}
          </Card>
          <div style={{fontSize:9,color:C.muted,marginTop:4}}>Golden dataset: 800 entries | 8 frameworks | 10 ESRS topics</div>
        </div>
      )}
    </div>
  );
}

// ═══ UC-15: M&A REGULATORY SHOCK ═══
function ShockTab() {
  const [profile,setProfile] = useState(null);
  const [fields,setFields] = useState({employee_count:"",jurisdiction:"",sector:"",listed:""});
  const [delta,setDelta] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");

  useEffect(() => { api("/entity/profile").then(r=>setProfile(r)).catch(()=>{}); }, []);

  const simulate = async () => {
    setLoading(true); setError("");
    try {
      const body = {};
      if(fields.employee_count) body.employee_count = parseInt(fields.employee_count);
      if(fields.jurisdiction) body.jurisdiction = fields.jurisdiction;
      if(fields.sector) body.sector = fields.sector;
      if(fields.listed) body.listed = fields.listed === "true";
      const r = await put("/entity/profile",body);
      setDelta(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div data-testid="shock-tab">
      {profile && (
        <Card color={C.dim} style={{marginBottom:12}}>
          <Label>Current Entity Profile</Label>
          <div style={{display:"flex",gap:16,fontSize:10,color:C.muted,flexWrap:"wrap"}}>
            <span>Name: <b style={{color:C.text}}>{profile.name}</b></span>
            <span>Employees: <b style={{color:C.text}}>{profile.employee_count?.toLocaleString()}</b></span>
            <span>Jurisdiction: <b style={{color:C.text}}>{profile.jurisdiction}</b></span>
            <span>Listed: <b style={{color:C.text}}>{profile.listed?"Yes":"No"}</b></span>
          </div>
        </Card>
      )}
      <Label>Simulate M&A / Entity Change</Label>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:6,marginBottom:10}}>
        <div><Label>New Employee Count</Label><Input value={fields.employee_count} onChange={v=>setFields(p=>({...p,employee_count:v}))} placeholder="e.g. 12000"/></div>
        <div><Label>New Jurisdiction</Label><Input value={fields.jurisdiction} onChange={v=>setFields(p=>({...p,jurisdiction:v}))} placeholder="e.g. US"/></div>
        <div><Label>New Sector</Label><Input value={fields.sector} onChange={v=>setFields(p=>({...p,sector:v}))} placeholder="e.g. chemicals"/></div>
        <div><Label>Listed?</Label>
          <select value={fields.listed} onChange={e=>setFields(p=>({...p,listed:e.target.value}))} style={{padding:"6px 10px",borderRadius:5,border:`1px solid ${C.border}`,background:C.surface,color:C.text,fontSize:11,width:"100%"}}>
            <option value="">No change</option><option value="true">Yes</option><option value="false">No</option>
          </select>
        </div>
      </div>
      <Btn data-testid="simulate-shock-btn" onClick={simulate} color={C.red} disabled={loading}>{loading?"Calculating...":"Simulate Regulatory Shock"}</Btn>
      <Err error={error}/>
      {delta && (
        <div data-testid="shock-results" style={{marginTop:12}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10}}>
            <Card color={C.emerald}><Label>New Mandatory</Label><Val color={C.emerald}>{delta.new_mandatory?.length || 0}</Val></Card>
            <Card color={C.red}><Label>Dropped</Label><Val color={C.red}>{delta.dropped?.length || 0}</Val></Card>
            <Card color={C.amber}><Label>Changed Fields</Label><Val color={C.amber}>{Object.keys(delta.changed_fields||{}).length}</Val></Card>
          </div>
          {(delta.new_mandatory||[]).map((fw,i)=>(
            <div key={i} style={{padding:"5px 8px",borderBottom:`1px solid ${C.border}20`,fontSize:10}}>
              <span style={{color:C.emerald,fontWeight:600}}>+ NEW: </span>
              <span style={{color:C.text}}>{fw.framework_id || fw} — {fw.reason || ""}</span>
            </div>
          ))}
          {(delta.dropped||[]).map((fw,i)=>(
            <div key={i} style={{padding:"5px 8px",borderBottom:`1px solid ${C.border}20`,fontSize:10}}>
              <span style={{color:C.red,fontWeight:600}}>- DROPPED: </span>
              <span style={{color:C.text}}>{fw.framework_id || fw}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ UC-17: EU TAXONOMY ═══
function TaxonomyTab() {
  const [naces,setNaces] = useState("C24,D35.11");
  const [eligible,setEligible] = useState(null);
  const [activityId,setActivityId] = useState("");
  const [metric,setMetric] = useState("85");
  const [alignment,setAlignment] = useState(null);
  const [loading,setLoading] = useState(false);
  const [summary,setSummary] = useState(null);
  const [error,setError] = useState("");

  useEffect(() => { api("/taxonomy/summary").then(r=>setSummary(r)).catch(()=>{}); }, []);

  const checkElig = async () => {
    setLoading(true); setError("");
    try {
      const codes = naces.split(",").map(s=>s.trim()).filter(Boolean);
      const r = await post("/taxonomy/check-eligibility",{nace_codes:codes});
      setEligible(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const checkAlign = async () => {
    if(!activityId) return;
    setLoading(true); setError("");
    try {
      const r = await post("/taxonomy/check-alignment",{activity_id:activityId,metrics:{ghg_intensity:parseFloat(metric)||0,dnsh_adaptation:true,dnsh_water:true,dnsh_pollution:true,dnsh_circular:true}});
      setAlignment(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  return (
    <div data-testid="taxonomy-tab">
      {summary && <Card color={C.dim} style={{marginBottom:10}}><div style={{display:"flex",gap:16,fontSize:10,color:C.muted}}>
        <span>Activities: <b style={{color:C.emerald}}>{summary.total_activities}</b></span>
        <span>Enabling: <b style={{color:C.blue}}>{summary.enabling}</b></span>
        <span>Transitional: <b style={{color:C.amber}}>{summary.transitional}</b></span>
      </div></Card>}
      <Label>Step 1: Check Eligibility</Label>
      <div style={{display:"flex",gap:6,marginBottom:12}}>
        <div style={{flex:1}}><Input value={naces} onChange={setNaces} placeholder="C24, D35.11"/></div>
        <Btn data-testid="check-eligibility-btn" onClick={checkElig} color={C.emerald}>{loading?"...":"Check Eligibility"}</Btn>
      </div>
      <Err error={error}/>
      {eligible && (
        <div data-testid="eligibility-results" style={{marginBottom:12}}>
          <Card color={C.emerald}><Label>Eligible Activities</Label><Val color={C.emerald}>{eligible.total_eligible}</Val></Card>
          {(eligible.eligible_activities||[]).slice(0,8).map((a,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"5px 8px",borderBottom:`1px solid ${C.border}20`,fontSize:10,cursor:"pointer"}} onClick={()=>setActivityId(a.activity_id)}>
              <span style={{color:C.text,fontWeight:activityId===a.activity_id?700:400}}>{a.activity_id} — {a.activity_name}</span>
              <span style={{color:C.muted}}>{a.sector}</span>
            </div>
          ))}
        </div>
      )}
      {activityId && <>
        <Label>Step 2: Check Alignment — {activityId}</Label>
        <div style={{display:"flex",gap:6,marginBottom:8}}>
          <div style={{flex:1}}><Input value={metric} onChange={setMetric} placeholder="GHG intensity (gCO2e/kWh)"/></div>
          <Btn data-testid="check-alignment-btn" onClick={checkAlign} color={C.emerald}>{loading?"...":"Check Alignment"}</Btn>
        </div>
        {alignment && (
          <Card data-testid="alignment-results" color={alignment.aligned?C.emerald:C.red}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
              <div><Label>Aligned</Label><Val color={alignment.aligned?C.emerald:C.red}>{alignment.aligned?"YES":"NO"}</Val></div>
              <div><Label>TSC</Label><Val color={alignment.tsc_passed?C.emerald:C.red}>{alignment.tsc_passed?"Pass":"Fail"}</Val></div>
              <div><Label>DNSH</Label><Val color={alignment.dnsh_passed?C.emerald:C.red}>{alignment.dnsh_passed?"Pass":"Fail"}</Val></div>
            </div>
            {(alignment.gaps||[]).map((g,i)=><div key={i} style={{fontSize:10,color:C.red,padding:"2px 0"}}>Warning: {g}</div>)}
          </Card>
        )}
      </>}
    </div>
  );
}

// ═══ UC-07: SUPPLIER CAMPAIGNS ═══
function SupplierTab() {
  const [suppliers,setSuppliers] = useState([]);
  const [campaigns,setCampaigns] = useState([]);
  const [loading,setLoading] = useState(true);
  const [newCampaign,setNewCampaign] = useState({name:"",description:"",campaign_type:"annual_survey"});
  const [error,setError] = useState("");

  useEffect(() => {
    Promise.all([api("/supplier/").catch(()=>({suppliers:[]})),api("/supplier/campaigns").catch(()=>({campaigns:[]}))]).then(([s,c]) => {
      setSuppliers(s.suppliers||[]); setCampaigns(c.campaigns||[]);
    }).finally(()=>setLoading(false));
  }, []);

  const createCampaign = async () => {
    setError("");
    try {
      await post("/supplier/campaigns",newCampaign);
      const c = await api("/supplier/campaigns"); setCampaigns(c.campaigns||[]);
      setNewCampaign({name:"",description:"",campaign_type:"annual_survey"});
    } catch(e) { setError(e.message); }
  };

  if(loading) return <Loader/>;
  return (
    <div data-testid="supplier-tab">
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
        <Card color={C.pink}><Label>Suppliers</Label><Val color={C.pink}>{suppliers.length}</Val></Card>
        <Card color={C.teal}><Label>Campaigns</Label><Val color={C.teal}>{campaigns.length}</Val></Card>
      </div>
      <Err error={error}/>
      <Label>Create Campaign</Label>
      <div style={{display:"flex",gap:6,marginBottom:12}}>
        <div style={{flex:2}}><Input value={newCampaign.name} onChange={v=>setNewCampaign(p=>({...p,name:v}))} placeholder="Campaign name"/></div>
        <div style={{flex:2}}><Input value={newCampaign.description} onChange={v=>setNewCampaign(p=>({...p,description:v}))} placeholder="Description"/></div>
        <Btn data-testid="create-campaign-btn" onClick={createCampaign} color={C.pink} disabled={!newCampaign.name}>Create</Btn>
      </div>
      {campaigns.map((c,i)=>(
        <Card key={i} color={C.pink} style={{padding:"8px 10px"}}>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <div style={{fontSize:11,fontWeight:600,color:C.text}}>{c.name}</div>
            <Badge text={c.status || c.campaign_type || "active"} color={C.pink}/>
          </div>
          <div style={{fontSize:9,color:C.muted}}>{c.description}</div>
        </Card>
      ))}
      {suppliers.length > 0 && <div style={{marginTop:12}}>
        <Label>Suppliers ({suppliers.length})</Label>
        {suppliers.slice(0,10).map((s,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 0",borderBottom:`1px solid ${C.border}20`,fontSize:10}}>
            <span style={{color:C.text}}>{s.name}</span>
            <span style={{color:C.muted}}>{s.country} | {s.risk_tier || "unassessed"}</span>
          </div>
        ))}
      </div>}
    </div>
  );
}

// ═══ UC-13: AUDITOR PORTAL ═══
function AuditorTab() {
  const [chain,setChain] = useState(null);
  const [trail,setTrail] = useState([]);
  const [evidence,setEvidence] = useState([]);
  const [loading,setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api("/events/verify-chain").catch(()=>({})),
      api("/audit/trail").catch(()=>({entries:[]})),
      api("/evidence/").catch(()=>({evidence:[]}))
    ]).then(([c,t,e])=>{
      setChain(c); setTrail(t.entries||t.trail||[]); setEvidence(e.evidence||[]);
    }).finally(()=>setLoading(false));
  }, []);

  if(loading) return <Loader text="Loading audit data..."/>;
  return (
    <div data-testid="auditor-tab">
      <Card color={chain?.valid?C.emerald:C.red}>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <div><Label>Hash Chain</Label><Val color={chain?.valid?C.emerald:C.red}>{chain?.valid?"VALID":"BROKEN"}</Val></div>
          <div><Label>Events Verified</Label><Val>{chain?.verified || 0}</Val></div>
          <div><Label>Chain Head</Label><Val style={{fontSize:10}}>{(chain?.chain_head||"—").slice(0,16)}...</Val></div>
        </div>
      </Card>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <div>
          <Label>Evidence Library ({evidence.length})</Label>
          {evidence.slice(0,8).map((e,i)=>(
            <div key={i} style={{padding:"4px 0",borderBottom:`1px solid ${C.border}20`,fontSize:10,display:"flex",gap:6,alignItems:"center"}}>
              <span style={{color:C.text}}>{e.name}</span>
              <span style={{color:C.muted}}>{e.topic}</span>
              <Badge text={e.verified?"Verified":"Pending"} color={e.verified?C.emerald:C.amber}/>
            </div>
          ))}
        </div>
        <div>
          <Label>Audit Trail ({trail.length})</Label>
          {trail.slice(0,8).map((t,i)=>(
            <div key={i} style={{padding:"4px 0",borderBottom:`1px solid ${C.border}20`,fontSize:10}}>
              <span style={{color:C.amber}}>{t.action}</span>
              <span style={{color:C.muted,marginLeft:6}}>{t.resource_type}/{(t.resource_id||"").slice(0,10)}</span>
              <span style={{color:C.dim,marginLeft:6}}>{t.actor}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══ UC-21: CSDDD / SUPPLIER ESG ═══
function CSDDDTab() {
  const [suppliers,setSuppliers] = useState([]);
  const [campaigns,setCampaigns] = useState([]);
  const [loading,setLoading] = useState(true);
  useEffect(() => {
    Promise.all([api("/supplier/").catch(()=>({suppliers:[]})),api("/supplier/campaigns").catch(()=>({campaigns:[]}))]).then(([s,c])=>{
      setSuppliers(s.suppliers||[]); setCampaigns(c.campaigns||[]);
    }).finally(()=>setLoading(false));
  }, []);
  if(loading) return <Loader/>;
  return (
    <div data-testid="csddd-tab">
      <Card color={C.red}><div style={{display:"flex",justifyContent:"space-between"}}>
        <div><Label>CSDDD Art. 6-8 Due Diligence</Label><Val color={C.red}>Value Chain Coverage</Val></div>
        <div><Label>Tier 1 Suppliers</Label><Val>{suppliers.length}</Val></div>
        <div><Label>Active Campaigns</Label><Val color={C.teal}>{campaigns.length}</Val></div>
      </div></Card>
      {suppliers.slice(0,10).map((s,i)=>(
        <Card key={i} style={{padding:"7px 10px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div><div style={{fontSize:11,fontWeight:600,color:C.text}}>{s.name}</div><div style={{fontSize:9,color:C.muted}}>{s.country} | {s.sector||"—"}</div></div>
          <Badge text={s.risk_tier||"unassessed"} color={s.risk_tier==="high"?C.red:s.risk_tier==="medium"?C.amber:C.emerald}/>
        </Card>
      ))}
    </div>
  );
}

// ═══ UC-22: CBAM ═══
function CBAMTab() {
  const [product,setProduct] = useState("steel");
  const [origin,setOrigin] = useState("TR");
  const [tonnes,setTonnes] = useState("500");
  const [result,setResult] = useState(null);
  const [loading,setLoading] = useState(false);
  const search = async () => {
    setLoading(true);
    try {
      const ef = await api(`/data/search/ef?q=${product}+${origin}&scope=1`);
      const factor = ef.results?.[0]?.value_total || 2.1;
      const etsPrice = 75;
      const embedded = parseFloat(tonnes) * factor;
      const liability = embedded * etsPrice;
      setResult({product,origin,tonnes:parseFloat(tonnes),factor,embedded:Math.round(embedded),etsPrice,liability:Math.round(liability),ef_results:ef.results||[]});
    } catch {}
    setLoading(false);
  };
  return (
    <div data-testid="cbam-tab">
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        <div style={{flex:1}}><Label>Product</Label><Input value={product} onChange={setProduct} placeholder="steel, aluminium..."/></div>
        <div style={{flex:1}}><Label>Origin Country</Label><Input value={origin} onChange={setOrigin} placeholder="TR, CN, IN..."/></div>
        <div style={{flex:1}}><Label>Tonnes</Label><Input value={tonnes} onChange={setTonnes} placeholder="500"/></div>
      </div>
      <Btn data-testid="cbam-estimate-btn" onClick={search} color={C.orange} disabled={loading}>{loading?"...":"Estimate CBAM Liability"}</Btn>
      {result && <div data-testid="cbam-results" style={{marginTop:12}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          <Card color={C.orange}><Label>Embedded Emissions</Label><Val color={C.orange}>{result.embedded.toLocaleString()} tCO2e</Val></Card>
          <Card color={C.amber}><Label>EU ETS Price</Label><Val color={C.amber}>EUR {result.etsPrice}/tCO2e</Val></Card>
          <Card color={C.red}><Label>CBAM Liability</Label><Val color={C.red}>EUR {result.liability.toLocaleString()}</Val></Card>
        </div>
        <div style={{fontSize:9,color:C.muted,marginTop:4}}>EF used: {result.factor} tCO2e/t | Source: {result.ef_results?.[0]?.source || "default"}</div>
      </div>}
    </div>
  );
}

// ═══ UC-23: PEER BENCHMARKING ═══
function BenchmarkTab() {
  const [nace,setNace] = useState("C24");
  const [country,setCountry] = useState("DE");
  const [results,setResults] = useState([]);
  const [loading,setLoading] = useState(false);
  const search = async () => {
    setLoading(true);
    try { const r = await api(`/data/search/benchmarks?nace=${nace}&country=${country}`); setResults(r.results||[]); } catch {}
    setLoading(false);
  };
  return (
    <div data-testid="benchmark-tab">
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        <div style={{flex:1}}><Label>NACE Code</Label><Input value={nace} onChange={setNace} placeholder="C24"/></div>
        <div style={{flex:1}}><Label>Country</Label><Input value={country} onChange={setCountry} placeholder="DE, FR, EU..."/></div>
        <div><Label>&nbsp;</Label><Btn data-testid="benchmark-search-btn" onClick={search} color={C.cyan}>{loading?"...":"Search"}</Btn></div>
      </div>
      {results.length>0 && results.map((r,i)=>(
        <Card key={i} style={{padding:"8px 10px"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div><div style={{fontSize:11,fontWeight:600,color:C.text}}>{r.metric}</div><div style={{fontSize:9,color:C.muted}}>{r.nace} | {r.country} | {r.source}</div></div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:14,fontWeight:700,color:C.cyan}}>{r.median} <span style={{fontSize:9,color:C.muted}}>{r.unit}</span></div>
              <div style={{fontSize:9,color:C.muted}}>P25: {r.p25 || "—"} | P75: {r.p75 || "—"}</div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ═══ UC-24: CONSISTENCY CHECK ═══
function ConsistencyTab() {
  const [frameworks,setFrameworks] = useState("CSRD/ESRS,GRI,CDP");
  const [metric,setMetric] = useState("scope1_emissions");
  const [result,setResult] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");
  const check = async () => {
    setLoading(true); setError("");
    try {
      const fws = frameworks.split(",").map(s=>s.trim());
      const r = await post("/ai/consistency/check",{frameworks:fws,metric_name:metric,entity_id:"default"});
      setResult(r);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };
  return (
    <div data-testid="consistency-tab">
      <div style={{display:"flex",gap:6,marginBottom:10}}>
        <div style={{flex:2}}><Label>Frameworks (comma-sep)</Label><Input value={frameworks} onChange={setFrameworks}/></div>
        <div style={{flex:1}}><Label>Metric</Label><Input value={metric} onChange={setMetric}/></div>
        <div><Label>&nbsp;</Label><Btn data-testid="consistency-check-btn" onClick={check} color={C.blue}>{loading?"...":"Check"}</Btn></div>
      </div>
      <Err error={error}/>
      {result && <div data-testid="consistency-results" style={{marginTop:8}}>
        <Card color={result.score>=90?C.emerald:result.score>=70?C.amber:C.red}>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <div><Label>Consistency Score</Label><Val color={result.score>=90?C.emerald:C.amber}>{result.score || 0}/100</Val></div>
            <div><Label>Discrepancies</Label><Val color={C.red}>{result.discrepancies?.length || 0}</Val></div>
            <div><Label>Frameworks Checked</Label><Val>{result.frameworks_checked || 0}</Val></div>
          </div>
        </Card>
        {(result.discrepancies||[]).map((d,i)=>(
          <div key={i} style={{padding:"5px 0",borderBottom:`1px solid ${C.border}20`,fontSize:10,color:C.amber}}>
            Warning: {d.message || d.description || JSON.stringify(d)}
          </div>
        ))}
      </div>}
    </div>
  );
}

// ═══ UC-27: PII DETECTION ═══
function PIITab() {
  const [text,setText] = useState("Contact Maria Schmidt at maria.schmidt@stahl-ag.com or call +49 151 12345678. Employee ID: DE-2024-0042.");
  const [scanResult,setScanResult] = useState(null);
  const [redacted,setRedacted] = useState(null);
  const [loading,setLoading] = useState(false);
  const [error,setError] = useState("");
  const scan = async () => {
    setLoading(true); setError(""); setRedacted(null);
    try { const r = await post("/pii/scan",{text}); setScanResult(r); } catch(e) { setError(e.message); }
    setLoading(false);
  };
  const redact = async () => {
    setLoading(true);
    try { const r = await post("/pii/redact",{text}); setRedacted(r); } catch(e) { setError(e.message); }
    setLoading(false);
  };
  return (
    <div data-testid="pii-tab">
      <Label>Text to Scan</Label>
      <TextArea value={text} onChange={setText} rows={3}/>
      <div style={{display:"flex",gap:6,marginTop:8}}>
        <Btn data-testid="pii-scan-btn" onClick={scan} color={C.amber} disabled={loading}>Scan for PII</Btn>
        <Btn data-testid="pii-redact-btn" onClick={redact} color={C.red} disabled={loading||!scanResult}>Redact All</Btn>
      </div>
      <Err error={error}/>
      {scanResult && <div data-testid="pii-results" style={{marginTop:10}}>
        <Card color={C.amber}>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <div><Label>PII Entities Found</Label><Val color={C.amber}>{scanResult.entities?.length || scanResult.count || 0}</Val></div>
            <div><Label>Types</Label><Val>{[...new Set((scanResult.entities||[]).map(e=>e.type||e.entity_type))].join(", ") || "—"}</Val></div>
          </div>
        </Card>
        {(scanResult.entities||[]).map((e,i)=>(
          <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"4px 8px",borderBottom:`1px solid ${C.border}20`,fontSize:10}}>
            <span style={{color:C.amber,fontWeight:600}}>{e.type || e.entity_type}</span>
            <span style={{color:C.text}}>{e.text || e.value}</span>
            <span style={{color:C.muted}}>pos {e.start}-{e.end}</span>
          </div>
        ))}
      </div>}
      {redacted && <div style={{marginTop:10}}>
        <Label>Redacted Text</Label>
        <Card color={C.emerald}><div style={{fontSize:11,color:C.text,lineHeight:1.5}}>{redacted.redacted_text || redacted.text}</div></Card>
      </div>}
    </div>
  );
}

// ═══ MAIN APP ═══
const TAB_COMPONENTS = {
  materiality: MaterialityTab, scope3: Scope3Tab, claims: ClaimsTab, crossmap: CrossMapTab,
  ef: EFSearchTab, dq: DQCheckTab, eval: EvalTab, shock: ShockTab, taxonomy: TaxonomyTab,
  supplier: SupplierTab, auditor: AuditorTab, csddd: CSDDDTab, cbam: CBAMTab,
  benchmark: BenchmarkTab, consistency: ConsistencyTab, pii: PIITab,
};

export default function DisclosureOSWiredHub() {
  const [tab,setTab] = useState("materiality");

  const ActiveTab = TAB_COMPONENTS[tab] || MaterialityTab;
  const activeInfo = TABS.find(t=>t.id===tab);

  return (
    <div data-testid="wired-hub-page" style={{background:C.bg,minHeight:"100vh",color:C.text,fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      <div style={{padding:"14px 20px 10px",borderBottom:`1px solid ${C.border}`,position:"sticky",top:0,zIndex:10,background:C.bg+"f0",backdropFilter:"blur(12px)"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
          <div style={{width:30,height:30,borderRadius:7,background:`linear-gradient(135deg,${C.teal},${C.blue})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:C.white}}>D</div>
          <div><div style={{fontSize:14,fontWeight:700}}>DisclosureOS</div><div style={{fontSize:8,color:C.muted,letterSpacing:1.2,textTransform:"uppercase"}}>Wired Hub — 16 User Journeys</div></div>
          <div style={{marginLeft:"auto",fontSize:9,color:C.dim}}>281 endpoints | 31 tables | 800 golden</div>
        </div>
        <div data-testid="wired-hub-tabs" style={{display:"flex",gap:3,flexWrap:"wrap"}}>
          {TABS.map(t => (
            <button data-testid={`tab-${t.id}`} key={t.id} onClick={()=>setTab(t.id)} style={{padding:"4px 8px",borderRadius:4,border:`1px solid ${tab===t.id?t.color:C.border}`,background:tab===t.id?t.color+"15":"transparent",color:tab===t.id?t.color:C.muted,fontSize:9,fontWeight:600,cursor:"pointer",transition:"all 0.15s",display:"flex",alignItems:"center",gap:3}}>
              <span style={{fontSize:8,fontWeight:800}}>{t.icon}</span>{t.label}
              <span style={{fontSize:7,color:C.dim}}>{t.uc}</span>
            </button>
          ))}
        </div>
      </div>
      <div style={{padding:"16px 20px 40px",maxWidth:1100,margin:"0 auto"}}>
        <div style={{marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:16,fontWeight:800,color:activeInfo?.color}}>{activeInfo?.icon}</span>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:activeInfo?.color}}>{activeInfo?.label}</div>
            <div style={{fontSize:9,color:C.muted}}>{activeInfo?.uc} — Wired to live backend API</div>
          </div>
        </div>
        <ActiveTab/>
      </div>
    </div>
  );
}
